/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * İş firması listesi + seçim: Core API `GET /api/users/me/companies`.
 * Uygulama kabuğu: `App` içinde `CompanyProvider` → `Layout` (senkron uydu ile aynı ağaç).
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { AuthSessionUnavailable, isSessionVerificationUnavailable } from './session-verification';
import axios from '../api/axios';
import {
  SELECTED_COMPANY_STORAGE_KEY,
  readSelectedCompanyIdFromStorage,
  setValidatedSelectedCompanyIdForRequest,
  getValidatedSelectedCompanyIdForRequest,
} from '../config/company-scope.config';
import { getApplicationCodeForApi } from '../config/app.config';
import { isApplicationBrowserAuthMode } from '../config/auth-principal-mode.config';
import { resolveCoreApiPath } from '../utils/coreAssetUrl';
import {
  readCompanyScopeCandidateFromSearch,
  stripCompanyScopeFromBrowserUrl,
} from '../utils/company-launch-context.utils';
import { normalizeLanguage, type SupportedLanguage } from '../utils/language.utils';
import { prefetchDmsImages, resolveCompanyLogoMediaScopeId } from '../dms';
import { Loading } from '../components/Loading';
import { getCompanyScopeSnapshot } from '../utils/company-scope-lifecycle.utils';

export interface Company {
  id: string;
  name: string;
  [key: string]: unknown;
}

export const SYSTEM_COMPANY_SCOPE_ID = '0';

const SYSTEM_SCOPE_SUPPORTED_APP_CODES = new Set(['CORE', 'APPS', 'DMS', 'SCHEDULER', 'OUTPUT', 'NOTIFICATION']);
const SYSTEM_ONLY_COMPANY_SCOPE_APP_CODES = new Set(['CORE']);
const SYSTEM_SCOPE_LABELS: Record<SupportedLanguage, string> = {
  tr: 'Sistem (0)',
  en: 'System (0)',
  ar: 'النظام (0)',
  de: 'System (0)',
  fr: 'Systeme (0)',
  es: 'Sistema (0)',
  it: 'Sistema (0)',
  ru: 'Система (0)',
  zh: '系统 (0)',
  ja: 'システム (0)',
};

const NO_COMPANY_ROLE_LABELS: Record<SupportedLanguage, string> = {
  tr: 'Firma yetkiniz henüz atanmadı',
  en: 'Your company access has not been assigned yet',
  ar: 'لم يتم تعيين صلاحية الشركة لك بعد',
  de: 'Ihr Firmenzugriff wurde noch nicht zugewiesen',
  fr: "Votre acces a l'entreprise n'a pas encore ete attribue",
  es: 'Su acceso a la empresa aun no ha sido asignado',
  it: "L'accesso all'azienda non e ancora stato assegnato",
  ru: 'Доступ к компании еще не назначен',
  zh: '您的公司权限尚未分配',
  ja: '会社へのアクセス権はまだ割り当てられていません',
};

export function canUseSystemCompanyScope(user: {
  permissions?: string[];
  roles?: string[];
  role?: string | number;
} | null | undefined, appCode = getApplicationCodeForApi()): boolean {
  if (!SYSTEM_SCOPE_SUPPORTED_APP_CODES.has(String(appCode).trim().toUpperCase())) return false;
  const permissions = user?.permissions ?? [];
  if (permissions.includes('*') || permissions.includes('system:admin')) return true;
  return false;
}

export function usesSystemOnlyCompanyScope(appCode = getApplicationCodeForApi()): boolean {
  return SYSTEM_ONLY_COMPANY_SCOPE_APP_CODES.has(String(appCode).trim().toUpperCase());
}

export function getSystemCompanyScopeLabel(language: SupportedLanguage): string {
  return SYSTEM_SCOPE_LABELS[language] ?? SYSTEM_SCOPE_LABELS.en;
}

function getCompanyDisplayName(c: Record<string, unknown>, lang: string): string {
  const translated =
    c.name ??
    (c as Record<string, unknown>)[`name${lang.charAt(0).toUpperCase() + lang.slice(1)}`] ??
    (c as { nameTranslations?: Record<string, string> }).nameTranslations?.[lang];
  const defaultVal =
    (c as { nameDefault?: unknown }).nameDefault ??
    (c as { defaultName?: unknown }).defaultName ??
    (c as { nameEn?: unknown }).nameEn ??
    c.name;
  const val =
    translated && String(translated).trim()
      ? String(translated).trim()
      : defaultVal
        ? String(defaultVal).trim()
        : '';
  return val || '-';
}

function getCompanyLogoRef(c: Record<string, unknown>): string | null {
  const direct = c.logo ?? c.logoUrl ?? c.logoSrc ?? c.image ?? c.imageUrl ?? c.icon;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  if (direct && typeof direct === 'object' && typeof (direct as { url?: unknown }).url === 'string') {
    const nested = String((direct as { url: string }).url).trim();
    return nested || null;
  }
  return null;
}

interface CompanyContextType {
  companies: Company[];
  selectedCompanyId: string;
  selectedCompany: Company | null;
  canAccessSystemScope: boolean;
  isSystemScope: boolean;
  systemScopeCompany: Company | null;
  setSelectedCompanyId: (id: string) => void;
  switchCompany: (companyId: string) => Promise<boolean>;
  isLoading: boolean;
  hasHydratedCompanies: boolean;
  /** Geriye dönük uyumluluk: ürün kodları yeni ada taşınana kadar aynı hydration durumudur. */
  companiesHydrated: boolean;
  error: string | null;
}

const COMPANY_CONTEXT_GLOBAL_KEY = 'qubepro_company_context';

function readSelectedCompanyIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return readCompanyScopeCandidateFromSearch(window.location.search);
}

function getStableCompanyContext(): React.Context<CompanyContextType | undefined> {
  const g = globalThis as typeof globalThis & {
    [COMPANY_CONTEXT_GLOBAL_KEY]?: React.Context<CompanyContextType | undefined>;
  };
  if (!g[COMPANY_CONTEXT_GLOBAL_KEY]) {
    g[COMPANY_CONTEXT_GLOBAL_KEY] = createContext<CompanyContextType | undefined>(undefined);
  }
  return g[COMPANY_CONTEXT_GLOBAL_KEY]!;
}

const CompanyContext = getStableCompanyContext();

const LANG_ONLY_DEBOUNCE_MS = 350;
/** Aynı kullanıcı + dil için ardışık spam istekleri kes (dev/HMR/çoklu tetikleyici) */
const COMPANIES_FETCH_COOLDOWN_MS = 30000;
/** Aynı dedupeKey ile üst üste yeni HTTP açmayı kes (inflight bittikten sonra tekrar) */
const COMPANIES_SAME_KEY_GAP_MS = 5000;
let companiesLastHttpDedupeKey = '';
let companiesLastHttpStartAt = 0;

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user, language, isInitialized, isAuthenticated, refreshUser, sessionVerificationUnavailable } = useAuth();
  const applicationAuthMode = isApplicationBrowserAuthMode();
  const normalizedLanguage: SupportedLanguage = normalizeLanguage(language);
  const userIdentity = user?.id || user?.email || '';
  const userId = userIdentity ? `${user?.tenantId ?? ''}:${userIdentity}` : '';
  const authorityKey = JSON.stringify([userId, user?.membershipId, user?.authVersion, user?.membershipVersion, user?.isAdmin]);
  const [scopeRevision, setScopeRevision] = useState(() => getCompanyScopeSnapshot().revision);
  const [profileScope, setProfileScope] = useState<{ revision: number; ready: boolean } | null>(null);
  const commitCompanyScope = useCallback((id: string | null, options: { allowSystemScope?: boolean } = {}) => {
    setValidatedSelectedCompanyIdForRequest(id, { ...options, authorityKey });
    setScopeRevision(getCompanyScopeSnapshot().revision);
  }, [authorityKey]);
  const canAccessSystemScope = canUseSystemCompanyScope(user);
  // CORE merkezi yetkilileri Sistem (0) kapsamını korur. Normal ACTIVE tenant
  // üyeleri ise gerçek UserRole company kapsamlarını yükler; rol yoksa fail-closed.
  const systemOnlyCompanyScope = usesSystemOnlyCompanyScope() && canAccessSystemScope;
  const systemScopeCompany: Company | null = canAccessSystemScope
    ? {
        id: SYSTEM_COMPANY_SCOPE_ID,
        name: getSystemCompanyScopeLabel(normalizedLanguage),
        isSystemScope: true,
        logo: null,
      }
    : null;
  const initialUrlCompanyIdRef = useRef<string | null>(readSelectedCompanyIdFromUrl());
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasHydratedCompanies, setHasHydratedCompanies] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyVerificationUnavailable, setCompanyVerificationUnavailable] = useState(false);
  const companiesFetchSeqRef = useRef(0);
  const prevUserIdRef = useRef<string>('');
  const hadCompaniesRef = useRef(false);
  const languageDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Aynı anahtar için tek HTTP; efekt cleanup ile iptal edilmez (fırtına önlemi). */
  const companiesSharedInflightRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const lastCompaniesFetchCooldownRef = useRef<{ key: string; at: number } | null>(null);

  useLayoutEffect(() => {
    // Launch query yalnız seçim adayı olarak bellekte tutulur; adres çubuğunda kalmaz.
    stripCompanyScopeFromBrowserUrl();
  }, []);

  useLayoutEffect(() => {
    if (prevUserIdRef.current !== userId || !selectedCompanyId || applicationAuthMode) return;
    if (selectedCompanyId === SYSTEM_COMPANY_SCOPE_ID && !canAccessSystemScope) return;
    commitCompanyScope(selectedCompanyId, { allowSystemScope: selectedCompanyId === SYSTEM_COMPANY_SCOPE_ID && canAccessSystemScope });
  }, [userId, selectedCompanyId, applicationAuthMode, canAccessSystemScope, commitCompanyScope]);

  const setSelectedCompanyId = useCallback((id: string) => {
    if (applicationAuthMode) return;
    const normalizedId = String(id ?? '').trim();
    const selectingSystem = normalizedId === SYSTEM_COMPANY_SCOPE_ID;
    if (systemOnlyCompanyScope && !selectingSystem) return;
    if (selectingSystem && !canAccessSystemScope) return;
    if (!selectingSystem && !companies.some((company) => company.id === normalizedId)) return;
    commitCompanyScope(normalizedId, {
      allowSystemScope: selectingSystem && canAccessSystemScope,
    });
    setSelectedCompanyIdState(normalizedId);
    try {
      if (normalizedId) localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, normalizedId);
      else localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [applicationAuthMode, canAccessSystemScope, companies, systemOnlyCompanyScope, commitCompanyScope]);

  useEffect(() => {
    if (applicationAuthMode) {
      companiesFetchSeqRef.current += 1;
      companiesSharedInflightRef.current.clear();
      setCompanies([]);
      commitCompanyScope(null);
      setSelectedCompanyIdState('');
      setIsLoading(false);
      setHasHydratedCompanies(true);
      setError(null);
      setCompanyVerificationUnavailable(false);
      stripCompanyScopeFromBrowserUrl();
      try {
        localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    if (!isInitialized || !isAuthenticated || !userId) {
      if (languageDebounceTimerRef.current) {
        clearTimeout(languageDebounceTimerRef.current);
        languageDebounceTimerRef.current = null;
      }
      companiesFetchSeqRef.current += 1;
      companiesSharedInflightRef.current.clear();
      lastCompaniesFetchCooldownRef.current = null;
      companiesLastHttpDedupeKey = '';
      companiesLastHttpStartAt = 0;
      prevUserIdRef.current = '';
      hadCompaniesRef.current = false;
      setCompanies([]);
      commitCompanyScope(null);
      setSelectedCompanyIdState('');
      setIsLoading(false);
      setHasHydratedCompanies(false);
      setError(null);
      setCompanyVerificationUnavailable(false);
      try {
        localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }

    if (systemOnlyCompanyScope) {
      if (languageDebounceTimerRef.current) {
        clearTimeout(languageDebounceTimerRef.current);
        languageDebounceTimerRef.current = null;
      }
      companiesFetchSeqRef.current += 1;
      companiesSharedInflightRef.current.clear();
      lastCompaniesFetchCooldownRef.current = null;
      companiesLastHttpDedupeKey = '';
      companiesLastHttpStartAt = 0;
      prevUserIdRef.current = userId;
      hadCompaniesRef.current = false;
      initialUrlCompanyIdRef.current = null;
      setCompanies([]);
      setIsLoading(false);
      setHasHydratedCompanies(true);
      setError(canAccessSystemScope ? null : NO_COMPANY_ROLE_LABELS[normalizedLanguage]);
      stripCompanyScopeFromBrowserUrl();

      if (canAccessSystemScope) {
        commitCompanyScope(SYSTEM_COMPANY_SCOPE_ID, {
          allowSystemScope: true,
        });
        setSelectedCompanyIdState((prev) => (
          prev === SYSTEM_COMPANY_SCOPE_ID ? prev : SYSTEM_COMPANY_SCOPE_ID
        ));
        try {
          localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, SYSTEM_COMPANY_SCOPE_ID);
        } catch {
          /* ignore */
        }
      } else {
        commitCompanyScope(null);
        setSelectedCompanyIdState('');
        try {
          localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
      return;
    }

    const userChanged = prevUserIdRef.current !== userId;

    const runFetch = () => {
      if (userChanged) {
        prevUserIdRef.current = userId;
        hadCompaniesRef.current = false;
        setCompanies([]);
        commitCompanyScope(null);
        setSelectedCompanyIdState('');
        setHasHydratedCompanies(false);
      }

      const translations = normalizedLanguage || 'en';
      const dedupeKey = `${userId}\n${translations}\n${canAccessSystemScope ? 'system' : 'business'}`;

      if (!userChanged) {
        const cool = lastCompaniesFetchCooldownRef.current;
        if (
          cool &&
          cool.key === dedupeKey &&
          Date.now() - cool.at < COMPANIES_FETCH_COOLDOWN_MS
        ) {
          return () => {};
        }
      }

      if (
        !userChanged &&
        dedupeKey === companiesLastHttpDedupeKey &&
        Date.now() - companiesLastHttpStartAt < COMPANIES_SAME_KEY_GAP_MS
      ) {
        return () => {};
      }
      companiesLastHttpDedupeKey = dedupeKey;
      companiesLastHttpStartAt = Date.now();

      const seq = ++companiesFetchSeqRef.current;

      const showBlockingLoading = userChanged || !hadCompaniesRef.current;
      if (showBlockingLoading) {
        setIsLoading(true);
      }
      setError(null);
      setCompanyVerificationUnavailable(false);

      let loadPromise = companiesSharedInflightRef.current.get(dedupeKey);
      if (!loadPromise) {
        const req = axios
          .get(resolveCoreApiPath('/api/users/me/companies'), {
            params: {
              appCode: getApplicationCodeForApi(),
              translations,
            },
          })
          .finally(() => {
            companiesSharedInflightRef.current.delete(dedupeKey);
          });
        companiesSharedInflightRef.current.set(dedupeKey, req);
        loadPromise = req;
      }

      void Promise.resolve(loadPromise)
        .then((raw) => {
          if (seq !== companiesFetchSeqRef.current) return;
          const response = raw as { data?: { data?: unknown; success?: boolean } };
          setCompanyVerificationUnavailable(false);
          const payload = response.data?.data || response.data;
          const normalized = Array.isArray(payload)
            ? payload.map((c: Record<string, unknown>) => ({
              ...c,
              id: String(c.id),
              name: getCompanyDisplayName(c, translations),
              logo: getCompanyLogoRef(c),
            }))
            : [];
          if (normalized.length > 0 || canAccessSystemScope) {
            hadCompaniesRef.current = normalized.length > 0;
            setCompanies((prev) => (JSON.stringify(prev) === JSON.stringify(normalized) ? prev : normalized));
            // Firma logosu <img>/Image isteği axios interceptor'ından geçmez; her ref kendi
            // company scope'u ile stabil URL'ye çevrilerek tek sahibi burada önceden yüklenir.
            prefetchDmsImages(
              normalized.map((company) => ({
                ref: company.logo,
                companyScope: resolveCompanyLogoMediaScopeId(company.id),
                systemMedia: true,
              })),
            );
            const urlCompanyId = initialUrlCompanyIdRef.current;
            const savedId = readSelectedCompanyIdFromStorage();
            const hasCompany = (id: string | null | undefined) => Boolean(
              id && (
                (id === SYSTEM_COMPANY_SCOPE_ID && canAccessSystemScope) ||
                normalized.some((c: Company) => String(c.id) === id)
              ),
            );
            let idToUse = normalized.length > 0
              ? String(normalized[0].id)
              : SYSTEM_COMPANY_SCOPE_ID;
            if (hasCompany(urlCompanyId)) {
              idToUse = urlCompanyId!;
            } else if (hasCompany(savedId)) {
              idToUse = savedId!;
            }
            initialUrlCompanyIdRef.current = null;
            commitCompanyScope(idToUse, {
              allowSystemScope: idToUse === SYSTEM_COMPANY_SCOPE_ID && canAccessSystemScope,
            });
            setSelectedCompanyIdState((prev) => (prev === idToUse ? prev : idToUse));
            stripCompanyScopeFromBrowserUrl();
            try {
              localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, idToUse);
            } catch {
              /* ignore */
            }
            lastCompaniesFetchCooldownRef.current = { key: dedupeKey, at: Date.now() };
          } else {
            hadCompaniesRef.current = false;
            setCompanies([]);
            commitCompanyScope(null);
            setSelectedCompanyIdState('');
            initialUrlCompanyIdRef.current = null;
            stripCompanyScopeFromBrowserUrl();
            try {
              localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
            } catch {
              /* ignore */
            }
            lastCompaniesFetchCooldownRef.current = { key: dedupeKey, at: Date.now() };
            setError(NO_COMPANY_ROLE_LABELS[normalizedLanguage]);
          }
          setHasHydratedCompanies(true);
        })
        .catch((err: unknown) => {
          if (seq !== companiesFetchSeqRef.current) return;
          const status =
            (err as { response?: { status?: number }; status?: number })?.response?.status ??
            (err as { status?: number })?.status;
          setCompanyVerificationUnavailable(isSessionVerificationUnavailable(err));
          if (status === 401 || status === 403) {
            hadCompaniesRef.current = false;
            setCompanies([]);
            commitCompanyScope(null);
            setSelectedCompanyIdState('');
            initialUrlCompanyIdRef.current = null;
            stripCompanyScopeFromBrowserUrl();
            setError(NO_COMPANY_ROLE_LABELS[normalizedLanguage]);
            try {
              localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
            } catch {
              /* ignore */
            }
            lastCompaniesFetchCooldownRef.current = { key: dedupeKey, at: Date.now() };
            setHasHydratedCompanies(true);
            return;
          }
          commitCompanyScope(null);
          setSelectedCompanyIdState('');
          console.error('Firmalar yüklenemedi:', err);
          setError(err instanceof Error ? err.message : 'Firmalar yüklenemedi');
          lastCompaniesFetchCooldownRef.current = { key: dedupeKey, at: Date.now() };
          setHasHydratedCompanies(true);
        })
        .finally(() => {
          if (seq === companiesFetchSeqRef.current) {
            setIsLoading(false);
          }
        });

      return () => {};
    };

    let cleanupFetch: (() => void) | undefined;

    if (userChanged) {
      if (languageDebounceTimerRef.current) {
        clearTimeout(languageDebounceTimerRef.current);
        languageDebounceTimerRef.current = null;
      }
      cleanupFetch = runFetch();
    } else {
      if (languageDebounceTimerRef.current) {
        clearTimeout(languageDebounceTimerRef.current);
        languageDebounceTimerRef.current = null;
      }
      languageDebounceTimerRef.current = setTimeout(() => {
        languageDebounceTimerRef.current = null;
        cleanupFetch = runFetch();
      }, LANG_ONLY_DEBOUNCE_MS);
    }

    return () => {
      if (languageDebounceTimerRef.current) {
        clearTimeout(languageDebounceTimerRef.current);
        languageDebounceTimerRef.current = null;
      }
      cleanupFetch?.();
    };
  }, [
    applicationAuthMode,
    isAuthenticated,
    isInitialized,
    userId,
    normalizedLanguage,
    canAccessSystemScope,
    systemOnlyCompanyScope,
    commitCompanyScope,
  ]);

  const switchCompany = useCallback(
    async (companyId: string): Promise<boolean> => {
      if (applicationAuthMode) return false;
      if (!companyId) return false;
      if (companyId === getValidatedSelectedCompanyIdForRequest()) return true;
      try {
        setError(null);
        setCompanyVerificationUnavailable(false);
        const selectingSystem = String(companyId) === SYSTEM_COMPANY_SCOPE_ID;
        if (systemOnlyCompanyScope && !selectingSystem) {
          setError('Core yalnız Sistem (0) kapsamında çalışır');
          return false;
        }
        if (selectingSystem && !canAccessSystemScope) {
          setError('Sistem kapsaminda islem yapma yetkiniz yok');
          return false;
        }
        if (!selectingSystem && !companies.some((company) => String(company.id) === String(companyId))) {
          setError('Bu firma kapsamında işlem yapma yetkiniz yok');
          return false;
        }
        setSelectedCompanyId(companyId);
        window.dispatchEvent(
          new CustomEvent('qubepro_companySwitch', {
            detail: { companyId },
          }),
        );
        return true;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Firma değiştirilemedi';
        setError(msg);
        return false;
      }
    },
    [
      canAccessSystemScope,
      applicationAuthMode,
      companies,
      selectedCompanyId,
      setSelectedCompanyId,
      systemOnlyCompanyScope,
    ],
  );

  const selectedCompany = applicationAuthMode
    ? null
    : selectedCompanyId === SYSTEM_COMPANY_SCOPE_ID
      ? systemScopeCompany
      : companies.find((c) => String(c.id) === String(selectedCompanyId)) || null;

  const authorityReady = isInitialized && isAuthenticated && Boolean(userId) && prevUserIdRef.current === userId;
  const hasSelectedCompany = Boolean(selectedCompany);
  useEffect(() => {
    if (applicationAuthMode || !authorityReady || !hasHydratedCompanies || !hasSelectedCompany) return;
    const scope = getCompanyScopeSnapshot();
    let cancelled = false;
    void refreshUser().then(profile => {
      if (!cancelled && !scope.signal.aborted && getCompanyScopeSnapshot().revision === scope.revision) {
        setProfileScope({ revision: scope.revision, ready: profile !== null });
      }
    }).catch(() => {
      if (!cancelled && !scope.signal.aborted) setProfileScope({ revision: scope.revision, ready: false });
    });
    return () => { cancelled = true; };
  }, [applicationAuthMode, authorityReady, authorityKey, hasHydratedCompanies, hasSelectedCompany, scopeRevision, refreshUser]);

  const value: CompanyContextType = {
    companies,
    selectedCompanyId,
    selectedCompany,
    canAccessSystemScope: applicationAuthMode ? false : canAccessSystemScope,
    isSystemScope: !applicationAuthMode && selectedCompanyId === SYSTEM_COMPANY_SCOPE_ID,
    systemScopeCompany: applicationAuthMode ? null : systemScopeCompany,
    setSelectedCompanyId,
    switchCompany,
    isLoading,
    hasHydratedCompanies,
    companiesHydrated: hasHydratedCompanies,
    error,
  };

  // Every protected product starts behind the same validated company boundary.
  // Public routes (including Login) must render once auth hydration has proven
  // that there is no active user; CompanyProvider wraps the whole router tree.
  // A locale refresh keeps a valid scope mounted, so unsaved form state survives.
  const unauthenticatedReady = isInitialized && !isAuthenticated;
  const profileReady = profileScope?.revision === scopeRevision && profileScope.ready;
  const profileDenied = profileScope?.revision === scopeRevision && !profileScope.ready;
  const scopeReady = applicationAuthMode || unauthenticatedReady || (authorityReady && hasHydratedCompanies && hasSelectedCompany && profileReady);
  const scopeDenied = authorityReady && hasHydratedCompanies && !isLoading && (!hasSelectedCompany || profileDenied);
  return <CompanyContext.Provider value={value}>
    {scopeReady ? <React.Fragment key={`${authorityKey}:${selectedCompanyId}:${scopeRevision}`}>{children}</React.Fragment> : scopeDenied
      ? (companyVerificationUnavailable || (profileDenied && sessionVerificationUnavailable)
        ? <AuthSessionUnavailable language={normalizedLanguage} onRetry={() => window.location.reload()} />
        : <div className="page-container"><p role="alert">{error || NO_COMPANY_ROLE_LABELS[normalizedLanguage]}</p></div>)
      : <Loading fullScreen />}
  </CompanyContext.Provider>;
}

/** Shared chrome can also render on standalone entry pages without a provider. */
export function useOptionalCompany() {
  return useContext(CompanyContext);
}

export function useCompany() {
  const context = useOptionalCompany();
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
